import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Whether the screen is locked.
 *
 * Its own store, not a flag inside the router, because two unrelated things raise it: the idle
 * timer, and the user choosing Lock screen from their own menu. A component that owns the state
 * cannot be told to lock by a menu three levels away without prop-drilling or an event bus.
 *
 * PERSISTED, DELIBERATELY. Someone who locks the screen and walks away has made a decision, and a
 * page reload must not undo it — otherwise the lock is defeated by F5. The escape is the same as
 * always: Sign out is on the lock screen, so a persisted lock can never strand anyone.
 *
 * Not part of the auth store: that one is cleared wholesale on logout, and a lock flag surviving
 * or not surviving that clear would be an accident either way rather than a decision.
 */
interface LockState {
  locked: boolean
  /** Set when the user chose to lock, as opposed to going idle. Only affects the wording. */
  manual: boolean
  /**
   * When the lock was raised, and whether the session behind it has been shown to be alive since.
   *
   * A LOCK IS ONLY MEANINGFUL FOR AS LONG AS THE SESSION IT FROZE. Persisting `locked` so a reload
   * cannot defeat it also persisted it past the session's death: coming back days later, the auth
   * store still read as authenticated because the tokens were still in localStorage — expired, but
   * present — so the app showed a lock screen promising "your session is still open" over a
   * session that had been gone for days, instead of the login screen.
   *
   * `verified` is false until the server confirms the session on this page load. Nothing is
   * claimed about the session until then.
   */
  lockedAt: number | null
  verified: boolean
  lock: (manual?: boolean) => void
  unlock: () => void
  markVerified: () => void
}

export const useLockStore = create<LockState>()(
  persist(
    (set) => ({
      locked: false,
      manual: false,
      lockedAt: null,
      verified: false,
      // A lock raised in THIS tab is over a session we just saw working, so it starts verified.
      lock: (manual = false) => set({ locked: true, manual, lockedAt: Date.now(), verified: true }),
      unlock: () => set({ locked: false, manual: false, lockedAt: null, verified: false }),
      markVerified: () => set({ verified: true }),
    }),
    {
      name: 'vs-lock',
      // `verified` is a fact about THIS page load and must never be restored from storage —
      // persisting it would put the reassurance back on a dead session, which is the bug.
      partialize: (s) => ({ locked: s.locked, manual: s.manual, lockedAt: s.lockedAt }),
    },
  ),
)
